import React from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { useGetProductsByIdsQuery } from "../../../../../api/productApi";
import GroupedProductItem from "./GroupedProductItem";

interface GroupedProductListProps {
  productIds: number[];
}

const GroupedProductListSkeleton: React.FC = () => (
  <ul className="grouped-product-content__list">
    {Array.from({ length: 3 }).map((_, i) => (
      <li key={i} className="grouped-product-content__item is-loading">
        <div className="grouped-product-content__item-info">
          <Skeleton circle height={40} width={40} />
          <div className="grouped-product-content__item-details">
            <Skeleton width="80%" />
            <Skeleton width="40%" />
          </div>
        </div>
        <div className="grouped-product-content__item-actions">
          <Skeleton width={50} height={36} />
          <Skeleton width={80} height={36} />
        </div>
      </li>
    ))}
  </ul>
);

const GroupedProductList: React.FC<GroupedProductListProps> = ({
  productIds,
}) => {
  const { t } = useTranslation();
  const {
    data: products,
    isLoading,
    isError,
  } = useGetProductsByIdsQuery(productIds);

  if (isLoading) {
    return <GroupedProductListSkeleton />;
  }

  if (isError || !products) {
    return <p>{t("productInfoDetails.groupedError")}</p>;
  }

  // Preserve the order from the backend
  const sortedProducts = productIds
    .map((id) => products.find((p) => p.id === id))
    .filter((p): p is NonNullable<typeof p> => p !== undefined);

  return (
    <>
      <h3 className="grouped-product-content__title">
        {t("productInfoDetails.groupedTitle")}
      </h3>
      <ul className="grouped-product-content__list">
        {sortedProducts.map(
          (product) =>
            product && <GroupedProductItem key={product.id} product={product} />
        )}
      </ul>
    </>
  );
};

export default GroupedProductList;
